import { FC } from 'react'
import PerFlexRow from '../../components/custom_components/FlexRow'
import PerText from '../../components/custom_components/Text'
import { SkillEmployeeStatusDto, SkillEmployeeStatusEnum } from '../../types'
import { useTranslation, Trans } from 'react-i18next'
import { useAuth } from '../../context/auth-context'
import { useDateTime } from '../../shared/useDateTime'
import { Tooltip, Card, Icon } from 'antd'
import PerIcon from '../../components/custom_components/Icon'
import PerButtonCircle from '../../components/custom_components/ButtonCircle'

interface SkillStatusLabelComponentProps {
  isProcessing: boolean
  skillStatusErrorVisible: boolean
  skillStatus?: SkillEmployeeStatusDto | null
}

export const SkillStatusLabelComponent: FC<SkillStatusLabelComponentProps> = ({ isProcessing, skillStatus, skillStatusErrorVisible }) => {
  const { t } = useTranslation()

  return (
    <Card size="small" style={{ background: 'white', display: 'inline-block' }} bordered={false}>
      <PerFlexRow align="middle">
        {!!isProcessing ? (
          <PerIcon size={24} name="loading_fill" color="secondary" spin />
        ) : (
          <SkillStatusIcon tooltipVisible skillStatus={skillStatus} size={24} />
        )}

        <PerFlexRow direction="column" ml="xxs">
          <PerText fontSize="s" fontWeight="bold">
            {t('profile.skillCenter.public.skillStatus')}
          </PerText>
          {skillStatusErrorVisible ? (
            <PerText fontSize="s" color="error">
              {t('profile.skillCenter.public.skillStatusError')}
            </PerText>
          ) : (
            <PerText fontSize="s">
              {t(`profile.skillCenter.public.label.${skillStatus ? skillStatus.skillStatus : SkillEmployeeStatusEnum.Unknown}`)}
            </PerText>
          )}
        </PerFlexRow>
      </PerFlexRow>
    </Card>
  )
}

export const SkillStatusDescription: FC<{
  skillStatus?: SkillEmployeeStatusDto | null
  skillStatusErrorVisible: boolean
  onClick: () => void
  isProcessing: boolean
  iconVisible?: boolean
}> = ({ skillStatus, skillStatusErrorVisible, onClick, isProcessing, iconVisible = true }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateTimeShort } = useDateTime(user)

  const refreshAllowed = (!skillStatus || skillStatus.expired) && !skillStatusErrorVisible

  return (
    <PerFlexRow align="middle" mt="xxs">
      {refreshAllowed && iconVisible && (
        <PerButtonCircle
          mr="xxxs"
          color="primary"
          variant="pastel"
          icon="view_fill"
          size="small"
          isProcessing={{ active: isProcessing }}
          style={{ cursor: 'default' }}
          customIconComponent={<Icon type="reload" style={{ fontSize: 12 }} />}
        />
      )}
      <PerText fontSize="s" appearance="quote">
        {!!skillStatus && t('profile.skillCenter.public.latestSyncDate', { date: formatDateTimeShort(skillStatus.latestSyncDate) })}
        {refreshAllowed && (
          <Trans
            i18nKey="profile.skillCenter.public.refreshStatusButtonDescription"
            components={[
              <div style={{ textDecoration: 'underline', cursor: 'pointer', display: 'inline', color: '#05cbce' }} onClick={onClick} />,
            ]}
          />
        )}
      </PerText>
    </PerFlexRow>
  )
}

export const SkillStatusIcon: FC<{ skillStatus?: SkillEmployeeStatusDto | null; size?: number; tooltipVisible: boolean }> = ({
  skillStatus,
  tooltipVisible,
  size = 24,
}) => {
  const { t } = useTranslation()

  return (
    <Tooltip
      title={
        tooltipVisible
          ? t(`profile.skillCenter.public.label.${skillStatus ? skillStatus.skillStatus : SkillEmployeeStatusEnum.Unknown}`)
          : undefined
      }
    >
      <span>
        {!skillStatus ? (
          <PerText>{t(`profile.skillCenter.public.na`)}</PerText>
        ) : skillStatus.skillStatus === SkillEmployeeStatusEnum.Completed ? (
          <SkillStatusCompleted size={size} />
        ) : skillStatus.skillStatus === SkillEmployeeStatusEnum.Outdated ? (
          <SkillStatusOutdated size={size} />
        ) : skillStatus.skillStatus === SkillEmployeeStatusEnum.Missing ? (
          <SkillStatusMissing size={size} />
        ) : (
          <SkillStatusCvIncompleteOrNearlyOutdated size={size} />
        )}
      </span>
    </Tooltip>
  )
}

export const SkillStatusCompleted: FC<{ size: number }> = ({ size }) => {
  return (
    <div style={{ width: `${size}px`, height: `${size}px` }}>
      <svg width="100%" height="auto" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="16" height="16" rx="8" fill="#4EC700" />
        <path
          d="M7.99998 3.9997C7.85623 3.9997 7.72498 4.08095 7.66248 4.21064L6.59061 6.41845L4.19686 6.77157C4.05623 6.79189 3.93904 6.89032 3.89529 7.02626C3.85154 7.1622 3.88748 7.30907 3.98748 7.40907L5.72342 9.13095L5.31404 11.5622C5.29061 11.7028 5.34842 11.845 5.46561 11.9294C5.58279 12.0138 5.73592 12.0231 5.86092 11.9559L8.00154 10.8122L10.139 11.9544C10.2656 12.0216 10.4187 12.0122 10.5344 11.9278C10.65 11.8434 10.7094 11.7013 10.6859 11.5606L10.2765 9.12939L12.0125 7.40907C12.114 7.30907 12.1484 7.16064 12.1047 7.02626C12.0609 6.89189 11.9437 6.79345 11.8031 6.77157L9.40936 6.41845L8.33748 4.21064C8.27342 4.08095 8.14373 3.9997 7.99998 3.9997ZM7.99998 5.23407L8.82029 6.9247C8.87498 7.03564 8.97967 7.11376 9.10311 7.13251L10.9515 7.40439L9.60936 8.73407C9.52342 8.82001 9.48279 8.94189 9.50311 9.0622L9.82029 10.9325L8.17654 10.0544C8.06561 9.99501 7.93279 9.99501 7.82342 10.0544L6.17967 10.9325L6.49529 9.06376C6.51561 8.94345 6.47654 8.82157 6.38904 8.73564L5.04686 7.40595L6.89529 7.13251C7.01717 7.11376 7.12342 7.0372 7.17811 6.9247L7.99998 5.23407Z"
          fill="white"
        />
      </svg>
    </div>
  )
}

export const SkillStatusOutdated: FC<{ size: number }> = ({ size }) => {
  return (
    <div style={{ width: `${size}px`, height: `${size}px` }}>
      <svg width="100%" height="auto" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M8.01562 16C5.14062 16 2.51562 14.5 1.07812 12C-0.359375 9.53125 -0.359375 6.5 1.07812 4C2.51562 1.53125 5.14062 0 8.01562 0C10.8594 0 13.4844 1.53125 14.9219 4C16.3594 6.5 16.3594 9.53125 14.9219 12C13.4844 14.5 10.8594 16 8.01562 16ZM8.01562 4C7.57812 4 7.26562 4.34375 7.26562 4.75V8.25C7.26562 8.6875 7.57812 9 8.01562 9C8.42188 9 8.76562 8.6875 8.76562 8.25V4.75C8.76562 4.34375 8.42188 4 8.01562 4ZM7.01562 11C7.01562 11.5625 7.45312 12 8.01562 12C8.54688 12 9.01562 11.5625 9.01562 11C9.01562 10.4688 8.54688 10 8.01562 10C7.45312 10 7.01562 10.4688 7.01562 11Z"
          fill="#F1086D"
        />
      </svg>
    </div>
  )
}

export const SkillStatusCvIncompleteOrNearlyOutdated: FC<{ size: number }> = ({ size }) => {
  return (
    <div style={{ width: `${size}px`, height: `${size}px` }}>
      <svg width="100%" height="auto" viewBox="0 0 34 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M17.0312 32C11.2812 32 6.03125 29 3.15625 24C0.28125 19.0625 0.28125 13 3.15625 8C6.03125 3.0625 11.2812 0 17.0312 0C22.7188 0 27.9688 3.0625 30.8438 8C33.7188 13 33.7188 19.0625 30.8438 24C27.9688 29 22.7188 32 17.0312 32Z"
          fill="#F49709"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M15.5 9.5C15.5 8.6875 16.125 8 17 8C17.8125 8 18.5 8.6875 18.5 9.5V16.5C18.5 17.375 17.8125 18 17 18C16.125 18 15.5 17.375 15.5 16.5V9.5ZM17 24C15.875 24 15 23.125 15 22C15 20.9375 15.875 20 17 20C18.0625 20 19 20.9375 19 22C19 23.125 18.0625 24 17 24Z"
          fill="white"
        />
      </svg>
    </div>
  )
}

export const SkillStatusMissing: FC<{ size: number }> = ({ size }) => {
  return (
    <div style={{ width: `${size}px`, height: `${size}px` }}>
      <svg width="100%" height="auto" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clipPath="url(#clip0_2231_85192)">
          <rect x="16" y="-7" width="5.6" height="32" transform="rotate(45 16 -7)" fill="#F1086D" />
          <rect x="20.667" y="-2.33301" width="5.6" height="32" transform="rotate(45 20.667 -2.33301)" fill="#F1086D" />
          <rect x="25.334" y="2.33398" width="5.6" height="32" transform="rotate(45 25.334 2.33398)" fill="#F1086D" />
          <rect x="30.0005" y="7.00098" width="5.6" height="32" transform="rotate(45 30.0005 7.00098)" fill="#F1086D" />
          <rect x="34.6675" y="11.668" width="5.6" height="32" transform="rotate(45 34.6675 11.668)" fill="#F1086D" />
          <mask id="path-7-outside-1_2231_85192" maskUnits="userSpaceOnUse" x="4" y="6" width="24" height="21" fill="black">
            <rect fill="white" x="4" y="6" width="24" height="21" />
            <path d="M7.92187 9.33854L10.5 11.3151V11C10.5 9.9974 11.3021 9.16667 12.3333 9.16667H17.0599C17.5469 9.16667 18.0052 9.36719 18.349 9.71094L20.9557 12.3177C21.2995 12.6615 21.5 13.1198 21.5 13.6068V19.9375L24.8802 22.6302C25.1953 22.8594 25.2526 23.2891 24.9948 23.5755C24.7656 23.8906 24.3359 23.9479 24.0495 23.6901L7.09115 10.3984C6.77604 10.1693 6.71875 9.73958 6.97656 9.45312C7.20573 9.13802 7.63542 9.08073 7.92187 9.33854ZM11.875 12.4036L20.125 18.8776V13.75H17.8333C17.3177 13.75 16.9167 13.349 16.9167 12.8333V10.5417H12.3333C12.0755 10.5417 11.875 10.7708 11.875 11V12.4036ZM19.6667 22.4583C19.7812 22.4583 19.9245 22.401 20.0104 22.3151L21.0703 23.1745C20.7552 23.5755 20.2396 23.8333 19.6667 23.8333H12.3333C11.3021 23.8333 10.5 23.0313 10.5 22V14.8099L11.875 15.8984V22C11.875 22.2578 12.0755 22.4583 12.3333 22.4583H19.6667Z" />
          </mask>
          <path
            d="M7.92187 9.33854L10.5 11.3151V11C10.5 9.9974 11.3021 9.16667 12.3333 9.16667H17.0599C17.5469 9.16667 18.0052 9.36719 18.349 9.71094L20.9557 12.3177C21.2995 12.6615 21.5 13.1198 21.5 13.6068V19.9375L24.8802 22.6302C25.1953 22.8594 25.2526 23.2891 24.9948 23.5755C24.7656 23.8906 24.3359 23.9479 24.0495 23.6901L7.09115 10.3984C6.77604 10.1693 6.71875 9.73958 6.97656 9.45312C7.20573 9.13802 7.63542 9.08073 7.92187 9.33854ZM11.875 12.4036L20.125 18.8776V13.75H17.8333C17.3177 13.75 16.9167 13.349 16.9167 12.8333V10.5417H12.3333C12.0755 10.5417 11.875 10.7708 11.875 11V12.4036ZM19.6667 22.4583C19.7812 22.4583 19.9245 22.401 20.0104 22.3151L21.0703 23.1745C20.7552 23.5755 20.2396 23.8333 19.6667 23.8333H12.3333C11.3021 23.8333 10.5 23.0313 10.5 22V14.8099L11.875 15.8984V22C11.875 22.2578 12.0755 22.4583 12.3333 22.4583H19.6667Z"
            fill="white"
          />
          <path
            d="M7.92187 9.33854L6.24946 11.1968L6.32265 11.2626L6.4008 11.3226L7.92187 9.33854ZM10.5 11.3151L8.97892 13.2991L13 16.3819V11.3151H10.5ZM21.5 19.9375H19V21.1422L19.9423 21.8929L21.5 19.9375ZM24.8802 22.6302L23.3225 24.5856L23.3654 24.6198L23.4098 24.652L24.8802 22.6302ZM24.9948 23.5755L23.1366 21.9031L23.0495 21.9998L22.973 22.1051L24.9948 23.5755ZM24.0495 23.6901L25.7219 21.8319L25.6586 21.775L25.5917 21.7225L24.0495 23.6901ZM7.09115 10.3984L8.63335 8.4308L8.59795 8.40305L8.56157 8.3766L7.09115 10.3984ZM6.97656 9.45312L8.8348 11.1255L8.92186 11.0288L8.9984 10.9236L6.97656 9.45312ZM11.875 12.4036H9.375V13.6197L10.3317 14.3704L11.875 12.4036ZM20.125 18.8776L18.5817 20.8443L22.625 24.0173V18.8776H20.125ZM20.125 13.75H22.625V11.25H20.125V13.75ZM16.9167 10.5417H19.4167V8.04167H16.9167V10.5417ZM20.0104 22.3151L21.5849 20.3732L19.8353 18.9546L18.2426 20.5473L20.0104 22.3151ZM21.0703 23.1745L23.0361 24.719L24.5572 22.7831L22.6448 21.2326L21.0703 23.1745ZM10.5 14.8099L12.0518 12.8498L8 9.64214L8 14.8099H10.5ZM11.875 15.8984H14.375V14.689L13.4268 13.9383L11.875 15.8984ZM6.4008 11.3226L8.97892 13.2991L12.0211 9.33109L9.44295 7.35453L6.4008 11.3226ZM13 11.3151V11H8V11.3151H13ZM13 11C13 11.3526 12.7082 11.6667 12.3333 11.6667V6.66667C9.89596 6.66667 8 8.64223 8 11H13ZM12.3333 11.6667H17.0599V6.66667H12.3333V11.6667ZM17.0599 11.6667C16.8474 11.6667 16.6799 11.5774 16.5812 11.4787L20.1167 7.94317C19.3305 7.15695 18.2463 6.66667 17.0599 6.66667V11.6667ZM16.5812 11.4787L19.188 14.0855L22.7235 10.5499L20.1167 7.94317L16.5812 11.4787ZM19.188 14.0855C19.0892 13.9868 19 13.8192 19 13.6068H24C24 12.4204 23.5097 11.3362 22.7235 10.5499L19.188 14.0855ZM19 13.6068V19.9375H24V13.6068H19ZM19.9423 21.8929L23.3225 24.5856L26.4379 20.6748L23.0577 17.9821L19.9423 21.8929ZM23.4098 24.652C22.6055 24.0671 22.3368 22.7917 23.1366 21.9031L26.853 25.2479C28.1684 23.7864 27.7851 21.6516 26.3506 20.6084L23.4098 24.652ZM22.973 22.1051C23.5579 21.3008 24.8333 21.0321 25.7219 21.8319L22.3771 25.5483C23.8386 26.8637 25.9734 26.4804 27.0166 25.046L22.973 22.1051ZM25.5917 21.7225L8.63335 8.4308L5.54894 12.3661L22.5073 25.6577L25.5917 21.7225ZM8.56157 8.3766C9.36585 8.96153 9.63457 10.2369 8.8348 11.1255L5.11833 7.78071C3.80293 9.24226 4.18623 11.377 5.62072 12.4203L8.56157 8.3766ZM8.9984 10.9236C8.41347 11.7278 7.1381 11.9965 6.24946 11.1968L9.59429 7.48031C8.13274 6.16491 5.99799 6.54821 4.95472 7.9827L8.9984 10.9236ZM10.3317 14.3704L18.5817 20.8443L21.6683 16.9109L13.4183 10.4369L10.3317 14.3704ZM22.625 18.8776V13.75H17.625V18.8776H22.625ZM20.125 11.25H17.8333V16.25H20.125V11.25ZM17.8333 11.25C18.6984 11.25 19.4167 11.9682 19.4167 12.8333H14.4167C14.4167 14.7297 15.937 16.25 17.8333 16.25V11.25ZM19.4167 12.8333V10.5417H14.4167V12.8333H19.4167ZM16.9167 8.04167H12.3333V13.0417H16.9167V8.04167ZM12.3333 8.04167C10.5956 8.04167 9.375 9.49146 9.375 11H14.375C14.375 11.5994 14.1257 12.0878 13.8125 12.4147C13.4849 12.7565 12.9701 13.0417 12.3333 13.0417V8.04167ZM9.375 11V12.4036H14.375V11H9.375ZM19.6667 24.9583C20.4367 24.9583 21.2181 24.643 21.7782 24.0829L18.2426 20.5473C18.4769 20.3131 18.7162 20.1819 18.8974 20.1073C19.0768 20.0334 19.3367 19.9583 19.6667 19.9583V24.9583ZM18.4359 24.257L19.4958 25.1164L22.6448 21.2326L21.5849 20.3732L18.4359 24.257ZM19.1045 21.6299C19.2789 21.408 19.4979 21.3333 19.6667 21.3333V26.3333C20.9812 26.3333 22.2315 25.7431 23.0361 24.719L19.1045 21.6299ZM19.6667 21.3333H12.3333V26.3333H19.6667V21.3333ZM12.3333 21.3333C12.482 21.3333 12.6592 21.3971 12.7977 21.5356C12.9362 21.6741 13 21.8513 13 22H8C8 24.412 9.92137 26.3333 12.3333 26.3333V21.3333ZM13 22V14.8099H8V22H13ZM8.94824 16.77L10.3232 17.8586L13.4268 13.9383L12.0518 12.8498L8.94824 16.77ZM9.375 15.8984V22H14.375V15.8984H9.375ZM9.375 22C9.375 23.6385 10.6948 24.9583 12.3333 24.9583V19.9583C13.4562 19.9583 14.375 20.8771 14.375 22H9.375ZM12.3333 24.9583H19.6667V19.9583H12.3333V24.9583Z"
            fill="#F1086D"
            mask="url(#path-7-outside-1_2231_85192)"
          />
        </g>
        <defs>
          <clipPath id="clip0_2231_85192">
            <rect width="32" height="32" rx="16" fill="white" />
          </clipPath>
        </defs>
      </svg>
    </div>
  )
}
